import React, { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { Organization } from 'types';
import { providerService, ProviderBusiness } from 'api/services/provider.service';

interface OrganizationSwitcherProps {
  isExpanded?: boolean;
}

const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({ isExpanded = true }) => {
  const { user, currentOrganization, switchOrganization } = useAuth();
  const [businesses, setBusinesses] = useState<ProviderBusiness[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // A non-provider gets 403 or an empty list here, which is not an error worth
  // showing — they simply have nothing beneath them and the switcher falls back
  // to their own memberships.
  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await providerService.businesses();
      if (alive && res.success && res.data) setBusinesses(res.data);
    })();
    return () => { alive = false; };
  }, []);

  if (!user) return null;

  // Own memberships PLUS the businesses a provider may act for.
  //
  // This gate used to be `user.organizations.length <= 1` against memberships
  // alone, and a provider admin holds exactly ONE — the provider itself. So the
  // switcher rendered nothing, there was no way to change the ambient
  // organisation, and every page without its own picker sent the provider org
  // in X-Organization-Id.
  //
  // That is not a cosmetic gap. It is why a template created from the templates
  // screen was filed against the provider — which has no WhatsApp config — and
  // came back "WhatsApp is not configured for this organization", and it is the
  // same shape as the agents that had to be moved by hand with a migration.
  const options: Organization[] = [
    ...(user.organizations ?? []),
    ...businesses
      .filter((b) => !(user.organizations ?? []).some((o) => o.id === b.id))
      .map((b) => ({
        id: b.id,
        name: b.name,
        type: 'business' as const,
        // Keep the caller's own role: a master admin acting for a business is
        // still a master admin (this used to demote everyone to service_admin).
        role: ((user.organizations?.[0]?.role as any) || 'service_admin'),
        business_id: b.businessId,
      })),
  ];

  if (options.length <= 1) return null;

  // Acting for a business that is not one of the user's own memberships:
  // say so plainly, so nobody edits a customer's data thinking it is their own.
  const actingFor = !!currentOrganization
    && !(user.organizations ?? []).some((o) => o.id === currentOrganization.id);

  const handleOrganizationSwitch = (org: Organization) => {
    if (org.id !== currentOrganization?.id) {
      switchOrganization(org);
    }
    setIsOpen(false);
  };

  const getOrgTypeBadge = (type: 'service' | 'business') => {
    if (type === 'service') {
      return <span className="tl-pill tl-pill-info">Service</span>;
    }
    return <span className="tl-pill tl-pill-coral">Business</span>;
  };

  const getRoleBadge = (role: string) => {
    const roleLabels: Record<string, string> = {
      master_admin: 'Master Admin',
      super_admin: 'Super Admin',
      service_admin: 'Admin',
      service_user: 'User',
      operator_admin: 'Admin',
      operator_user: 'User'
    };
    return roleLabels[role] || role;
  };

  // Compact view for collapsed sidebar
  if (!isExpanded) {
    return (
      <div className="relative mb-3" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full p-2 rounded-xl transition flex items-center justify-center"
          style={{ background: 'var(--surface-raised)', border: '1px solid var(--border)' }}
          title={`${currentOrganization?.name ?? 'Organization'}${actingFor ? ' (acting as this business)' : ''} — switch (${options.length})`}
        >
          <Building2 className="w-5 h-5" style={{ color: 'var(--accent)' }} />
        </button>

        {isOpen && (
          <div className="absolute left-full ml-2 bottom-0 w-64 rounded-xl shadow-lg z-[9999] max-h-80 overflow-y-auto tl-card-flush">
            <div className="p-2">
              <div className="text-xs font-medium text-gray-500 px-3 py-2">
                Switch Organization
              </div>
              {options.map((org) => (
                <button
                  key={org.id}
                  onClick={() => handleOrganizationSwitch(org)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition ${
                    org.id === currentOrganization?.id
                      ? 'bg-blue-50 text-blue-700'
                      : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{org.name}</span>
                      {getOrgTypeBadge(org.type)}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{getRoleBadge(org.role)}</span>
                      {org.business_id && (
                        <span className="text-xs text-gray-400">• {org.business_id}</span>
                      )}
                    </div>
                  </div>
                  {org.id === currentOrganization?.id && (
                    <Check className="w-4 h-4" style={{ color: 'var(--accent)' }} />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Expanded view for open sidebar
  return (
    <div className="relative mb-3" ref={dropdownRef}>
      <div className="text-xs font-medium mb-2 px-1" style={{ color: 'var(--text-muted)' }}>
        Organization ({options.length})
      </div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl transition"
        title={currentOrganization ? `Current organisation: ${currentOrganization.name}` : undefined}
        style={{ background: 'var(--surface-raised)', border: `1px solid ${actingFor ? 'var(--accent)' : 'var(--border)'}` }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent)' }} />
          <div className="flex flex-col items-start min-w-0">
            <span className="text-sm font-medium truncate max-w-full" style={{ color: 'var(--text-primary)' }}>
              {currentOrganization?.name || 'Select Organization'}
            </span>
            {currentOrganization && (
              actingFor ? (
                <span className="text-xs font-medium" style={{ color: 'var(--accent)' }} data-testid="acting-for">
                  Acting as this business
                </span>
              ) : (
                <span className="text-xs text-gray-400">
                  {getRoleBadge(currentOrganization.role)}
                </span>
              )
            )}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-2 rounded-xl shadow-lg z-[9999] max-h-80 overflow-y-auto tl-card-flush">
          <div className="p-2">
            <div className="text-xs font-medium text-gray-500 px-3 py-2">
              Switch Organization
            </div>
            {options.map((org) => (
              <button
                key={org.id}
                onClick={() => handleOrganizationSwitch(org)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition ${
                  org.id === currentOrganization?.id
                    ? 'bg-blue-50 text-blue-700'
                    : 'hover:bg-gray-50 text-gray-900'
                }`}
              >
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{org.name}</span>
                    {getOrgTypeBadge(org.type)}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{getRoleBadge(org.role)}</span>
                    {org.business_id && (
                      <span className="text-xs text-gray-400">• {org.business_id}</span>
                    )}
                  </div>
                </div>
                {org.id === currentOrganization?.id && (
                  <Check className="w-4 h-4" style={{ color: 'var(--accent)' }} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSwitcher;
